import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { api } from '../api';
import type { DashboardAdminDetail } from '../types';

export default function DashboardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<DashboardAdminDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    api<DashboardAdminDetail>(`/admin/dashboards/${id}`)
      .then(setDetail)
      .catch(() => navigate('/dashboards'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="loading-state"><div className="spinner" /></div>;
  if (!detail) return null;

  const fmtDate = (dt: string | null) =>
    dt ? new Date(dt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const fmtDateTime = (dt: string | null) =>
    dt
      ? new Date(dt).toLocaleString('en', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : '—';

  const stats = detail.article_stats ?? {};
  const tok = detail.token_usage_30d ?? {};
  const maxSource = detail.source_breakdown?.[0]?.article_count || 1;

  const intentProfileEntries = Object.entries(detail.intent_profile ?? {}).filter(
    ([k]) => !['prompt', 'keywords', 'negative_keywords', 'coverage_mode'].includes(k)
  );

  function briefingPeriod(b: { period_start: string; period_end: string }) {
    const s = new Date(b.period_start).toLocaleDateString('en', { month: 'short', day: 'numeric' });
    const e = new Date(b.period_end).toLocaleDateString('en', { month: 'short', day: 'numeric' });
    return `${s} – ${e}`;
  }

  function statusBadgeClass(status: string) {
    if (status === 'completed' || status === 'done') return 'badge badge-ok';
    if (status === 'failed' || status === 'error') return 'badge badge-alert';
    if (status === 'running' || status === 'in_progress') return 'badge badge-accent';
    return 'badge badge-neutral';
  }

  return (
    <div className="page-content">
      <button className="back-link" onClick={() => navigate('/dashboards')}>← Back to Dashboards</button>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div className="row" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <h1 className="page-title">{detail.name}</h1>
          {detail.is_public && <span className="badge badge-accent">Public</span>}
          {detail.fetch_enabled
            ? <span className="badge badge-ok">Auto-fetch On</span>
            : <span className="badge badge-neutral">Auto-fetch Off</span>}
          {detail.coverage_mode && (
            <span className="badge badge-neutral" style={{ textTransform: 'capitalize' }}>
              {detail.coverage_mode.replace('_', ' ')}
            </span>
          )}
        </div>
        {detail.description && (
          <p style={{ fontSize: 13, color: 'var(--tx2)', maxWidth: 680, marginBottom: 8 }}>
            {detail.description}
          </p>
        )}
        <div style={{ fontSize: 12, color: 'var(--tx3)' }}>
          Owner: <strong style={{ color: 'var(--tx2)' }}>{detail.owner_email}</strong>
          {detail.owner_full_name && ` · ${detail.owner_full_name}`}
          {' · '}Fetch: {detail.fetch_frequency}
        </div>
      </div>

      {/* ── KPI row ────────────────────────────────────────────────── */}
      <div className="kpi-grid kpi-grid-5" style={{ marginBottom: 24 }}>
        <div className="kpi-cell">
          <div className="kpi-label">Articles (7d)</div>
          <div className="kpi-value">{(stats.total_7d ?? detail.article_count_7d).toLocaleString()}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Articles (30d)</div>
          <div className="kpi-value">{(stats.total_30d ?? 0).toLocaleString()}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">All Time</div>
          <div className="kpi-value">{(stats.total_all ?? 0).toLocaleString()}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Narratives</div>
          <div className="kpi-value">{detail.narrative_count.toLocaleString()}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">Language</div>
          <div className="kpi-value" style={{ fontSize: 16, textTransform: 'uppercase' }}>
            {detail.extraction_language}
          </div>
        </div>
      </div>

      {/* ── Intent / configuration ─────────────────────────────────── */}
      {(detail.prompt || detail.keywords?.length || detail.negative_keywords?.length || intentProfileEntries.length > 0) && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Search Configuration</div>
          <div className="grid-2" style={{ gap: 20 }}>
            <div>
              {detail.prompt && (
                <div style={{ marginBottom: 16 }}>
                  <div className="overline" style={{ marginBottom: 6 }}>Prompt</div>
                  <div className="prose-box">{detail.prompt}</div>
                </div>
              )}
              {detail.keywords?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div className="overline" style={{ marginBottom: 4 }}>Keywords</div>
                  <div className="pill-list">
                    {detail.keywords.map(k => <span key={k} className="pill">{k}</span>)}
                  </div>
                </div>
              )}
              {detail.negative_keywords?.length > 0 && (
                <div>
                  <div className="overline" style={{ marginBottom: 4 }}>Excluded Keywords</div>
                  <div className="pill-list">
                    {detail.negative_keywords.map(k => <span key={k} className="pill pill-negative">{k}</span>)}
                  </div>
                </div>
              )}
            </div>
            {intentProfileEntries.length > 0 && (
              <div>
                <div className="overline" style={{ marginBottom: 8 }}>Intent Profile</div>
                <div className="kv-grid">
                  {intentProfileEntries.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <span className="kv-key">{k.replace(/_/g, ' ')}</span>
                      <span className="kv-value">
                        {Array.isArray(v) ? v.join(', ') : String(v)}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Article stats ──────────────────────────────────────────── */}
      {stats.total_all != null && (
        <div className="grid-2" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-title">Article Volume</div>
            <div className="stat-row">
              <span className="stat-row-label">Today</span>
              <span className="stat-row-value">{(stats.total_24h ?? 0).toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">Last 7 days</span>
              <span className="stat-row-value">{(stats.total_7d ?? 0).toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">Last 30 days</span>
              <span className="stat-row-value">{(stats.total_30d ?? 0).toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">All time</span>
              <span className="stat-row-value">{(stats.total_all ?? 0).toLocaleString()}</span>
            </div>
          </div>
          <div className="card">
            <div className="card-title">Content Quality</div>
            <div className="stat-row">
              <span className="stat-row-label">Avg Objectivity</span>
              <span className="stat-row-value">
                {stats.avg_objectivity != null ? (stats.avg_objectivity * 100).toFixed(1) + '%' : '—'}
              </span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">Avg Emotional Intensity</span>
              <span className="stat-row-value">
                {stats.avg_emotional_intensity != null
                  ? (stats.avg_emotional_intensity * 100).toFixed(1) + '%'
                  : '—'}
              </span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">Created</span>
              <span className="stat-row-value">{fmtDate(detail.created_at)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">Last Active</span>
              <span className="stat-row-value">{fmtDateTime(detail.last_triggered_at)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Activity chart ─────────────────────────────────────────── */}
      {detail.activity_timeline?.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Article Volume — Last 14 Days</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={detail.activity_timeline} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-s)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--tx3)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--tx3)' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', boxShadow: 'none' }}
                cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
              />
              <Area type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={2} fill="url(#grad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Top Narratives / Topics ────────────────────────────────── */}
      {detail.top_narratives?.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Top Narratives</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Description</th>
                <th>Articles</th>
                <th>Last Seen</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.top_narratives.map(n => (
                <tr key={n.topic_id}>
                  <td style={{ fontWeight: 600, maxWidth: 180 }}>{n.name}</td>
                  <td>
                    {n.category
                      ? <span className="tag">{n.category}</span>
                      : <span style={{ color: 'var(--tx4)' }}>—</span>}
                  </td>
                  <td style={{ color: 'var(--tx2)', maxWidth: 260, fontSize: 12 }}>
                    {n.description
                      ? n.description.slice(0, 120) + (n.description.length > 120 ? '…' : '')
                      : <span style={{ color: 'var(--tx4)' }}>—</span>}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{n.article_count.toLocaleString()}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx3)' }}>{fmtDate(n.last_article_at)}</td>
                  <td>
                    {n.is_trending
                      ? <span className="badge badge-spiking">Trending</span>
                      : n.is_active
                        ? <span className="badge badge-ok">Active</span>
                        : <span className="badge badge-neutral">Quiet</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Source breakdown ───────────────────────────────────────── */}
      {detail.source_breakdown?.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Top Sources (30 days)</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Source</th>
                <th>Articles</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {detail.source_breakdown.map((s, i) => (
                <tr key={s.source_name}>
                  <td style={{ color: 'var(--tx3)', width: 32 }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{s.source_name}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.article_count.toLocaleString()}</td>
                  <td>
                    <div className="mini-bar-wrap">
                      <div className="mini-bar-track">
                        <div
                          className="mini-bar-fill"
                          style={{ width: `${Math.round((s.article_count / maxSource) * 100)}%` }}
                        />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--tx2)', minWidth: 36 }}>
                        {((s.article_count / maxSource) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Recent Briefings ───────────────────────────────────────── */}
      {detail.recent_briefings?.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Recent Briefings</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Title</th>
                <th>Status</th>
                <th>Period</th>
                <th>Model</th>
                <th>Tokens In</th>
                <th>Tokens Out</th>
              </tr>
            </thead>
            <tbody>
              {detail.recent_briefings.map(b => (
                <tr key={b.id}>
                  <td style={{ fontSize: 12, color: 'var(--tx3)', whiteSpace: 'nowrap' }}>{fmtDateTime(b.created_at)}</td>
                  <td style={{ fontWeight: 500, maxWidth: 220 }}>
                    {b.title ?? <span style={{ color: 'var(--tx4)' }}>Untitled</span>}
                  </td>
                  <td><span className={statusBadgeClass(b.status)}>{b.status}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--tx2)', whiteSpace: 'nowrap' }}>
                    {briefingPeriod(b)}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--tx3)' }}>{b.llm_model ?? '—'}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                    {b.tokens_in != null ? b.tokens_in.toLocaleString() : '—'}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                    {b.tokens_out != null ? b.tokens_out.toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Recent Ingestions ──────────────────────────────────────── */}
      {detail.recent_ingestions?.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-title">Recent Ingestions</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Name</th>
                <th>Status</th>
                <th>URLs</th>
                <th>Done / Failed</th>
                <th>Lang</th>
              </tr>
            </thead>
            <tbody>
              {detail.recent_ingestions.map(ing => (
                <tr key={ing.id}>
                  <td style={{ fontSize: 12, color: 'var(--tx3)', whiteSpace: 'nowrap' }}>{fmtDateTime(ing.created_at)}</td>
                  <td style={{ fontWeight: 500, maxWidth: 200 }}>
                    {ing.name ?? <span style={{ color: 'var(--tx4)' }}>—</span>}
                  </td>
                  <td><span className={statusBadgeClass(ing.status)}>{ing.status}</span></td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{ing.total_urls.toLocaleString()}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                    <span style={{ color: 'var(--ok)' }}>{ing.completed_count}</span>
                    {' / '}
                    <span style={{ color: 'var(--alert)' }}>{ing.failed_count}</span>
                  </td>
                  <td style={{ textTransform: 'uppercase', fontSize: 12, color: 'var(--tx3)' }}>
                    {ing.language}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Token Usage (30d) ──────────────────────────────────────── */}
      {tok.total != null && (
        <div className="card">
          <div className="card-title">LLM Token Usage (30 days)</div>
          <div className="kpi-grid kpi-grid-4" style={{ marginBottom: 0 }}>
            <div className="kpi-cell">
              <div className="kpi-label">Input Tokens</div>
              <div className="kpi-value accent">{(tok.tokens_in ?? 0).toLocaleString()}</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">Output Tokens</div>
              <div className="kpi-value accent">{(tok.tokens_out ?? 0).toLocaleString()}</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">Total Tokens</div>
              <div className="kpi-value">{(tok.total ?? 0).toLocaleString()}</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">Est. Cost</div>
              <div className="kpi-value alert">${(tok.cost ?? 0).toFixed(4)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
